import DateRangePickerNew from "components/common/DateRangePicker/DateRangePickerNew";
import React, { useEffect, useState, useCallback, useRef } from "react";
import HistoryTable from "./HistoryTable";
import { apiGetRequest } from "helpers/Requests";
import {
  URL_PARTICIPANT_GET_MEDICATION_LIST,
  URL_PARTICIPANT_GET_MEDICATION_LIST_HRM,
  URL_PARTICIPANT_MEDICATION_TABLE_LIST,
  URL_PARTICIPANT_MEDICATION_TABLE_LIST_HRM,
  URL_PARTICIPANT_MEDICATION_HISTORY_EXPORT,
  URL_PARTICIPANT_MEDICATION_HISTORY_EXPORT_HRM,
} from "store/services/URL";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import moment from "moment";
import { addMonths } from "date-fns";
import { roleAccess } from "helpers/RolesPermission";
import { USER_ROLE } from "Constants/constant";
import Select from "react-select";
import axios from "store/services/axios";
import { getToken } from "helpers/Tokens";
import { getJourneyHeaders } from "Utils/journeyHeaders";
import { toast } from "react-toastify";
import {
  ReportExportButton,
  ReportFilterField,
  ReportFiltersShell,
} from "pages/Reporting/reportFilterUi";
import CustomButton from "components/common/Button/CustomButton";
import { BsFilterCircle } from "react-icons/bs";
import { downloadMedicationHistoryExport } from "./medicationHistoryExportDownload";

const ALL_OPTION = { label: "All", value: "__all__" };

const MedicationHistory = ({ participantInfo }) => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [medicinList, setMedicinList] = useState([]);
  const [loader, setLoader] = useState(false);
  const [selectedFromDate, setSelectedFromDate] = useState(null);
  const [medicationOptions, setMedicationOptions] = useState([ALL_OPTION]);
  const [selectedMedications, setSelectedMedications] = useState([ALL_OPTION]);
  const [medicationOptionsLoading, setMedicationOptionsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const didInitialFetchRef = useRef(false);

  useEffect(() => {
    const currentMonthStart = moment().startOf("month").format("YYYY-MM-DD");
    const currentMonthEnd = moment().endOf("month").format("YYYY-MM-DD");

    setFromDate(currentMonthStart);
    setToDate(currentMonthEnd);
  }, []);  

  const isAllMedicationsSelected = useCallback(() => {
    return (
      selectedMedications?.length === 1 && selectedMedications[0]?.value === "__all__"
    );
  }, [selectedMedications]);

  const filterBySelectedMedications = useCallback(
    (list) => {
      if (!Array.isArray(list)) {
        return [];
      }
      if (isAllMedicationsSelected() || !selectedMedications?.length) {
        return list;
      }
      const selectedUuids = new Set(selectedMedications.map((m) => m.value));
      return list.filter((item) => selectedUuids.has(item?.uuid));
    },
    [isAllMedicationsSelected, selectedMedications]
  );

  const fetchMedicationList = useCallback(() => {
    if (fromDate && toDate && participantInfo?.uuid) {
      setLoader(true);
      const URL = roleAccess([USER_ROLE?.Sp_Standard_User])
        ? URL_PARTICIPANT_GET_MEDICATION_LIST_HRM
        : URL_PARTICIPANT_GET_MEDICATION_LIST;
      const url = `${URL}?pt_id=${participantInfo?.uuid}&duration_start=${fromDate}&duration_end=${toDate}`;

      apiGetRequest(url)
        .then((res) => {
          setMedicinList(filterBySelectedMedications(res?.data?.data || []));
        })
        .catch((error) => {
          // Error handled by loader state
        })
        .finally(() => {
          setLoader(false);
        });
    }
  }, [fromDate, toDate, participantInfo?.uuid, filterBySelectedMedications]);

  const fetchMedicationOptions = useCallback(() => {
    if (!participantInfo?.uuid) {
      return;
    }
    setMedicationOptionsLoading(true);
    const URL = roleAccess([USER_ROLE?.Sp_Standard_User])
      ? URL_PARTICIPANT_MEDICATION_TABLE_LIST_HRM
      : URL_PARTICIPANT_MEDICATION_TABLE_LIST;

    apiGetRequest(`${URL}?pt_id=${participantInfo?.uuid}`)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res?.data?.data)) {
          const opts = res.data.data
            .filter((item) => item?.id != null && item?.uuid)
            .map((item) => ({
              label: item?.medicine_name || "Medication",
              value: item?.uuid,
            }));
          setMedicationOptions([ALL_OPTION, ...opts]);
        } else {
          setMedicationOptions([ALL_OPTION]);
        }
      })
      .catch(() => {
        setMedicationOptions([ALL_OPTION]);
      })
      .finally(() => setMedicationOptionsLoading(false));
  }, [participantInfo?.uuid]);

  useEffect(() => {
    fetchMedicationOptions();
  }, [fetchMedicationOptions]);

  useEffect(() => {
    didInitialFetchRef.current = false;
  }, [participantInfo?.uuid]);

  // Load current month once; later list updates only via Search (date + medication).
  useEffect(() => {
    if (!fromDate || !toDate || !participantInfo?.uuid || didInitialFetchRef.current) {
      return;
    }
    didInitialFetchRef.current = true;
    fetchMedicationList();
  }, [fromDate, toDate, participantInfo?.uuid, fetchMedicationList]);

  const handleDateChange = (obj) => {
    if (obj?.fromDate) setFromDate(obj.fromDate);
    if (obj?.toDate) setToDate(obj.toDate);
  };

  const handleSearch = () => {
    if (!fromDate || !toDate) {
      toast.error("Please select a start and end date.");
      return;
    }
    fetchMedicationList();
  };

  const handleMedicationFilterChange = (selected) => {
    const next = selected || [];

    if (next.length === 0) {
      setSelectedMedications([ALL_OPTION]);
      return;
    }

    const hasAll = next.some((option) => option.value === "__all__");
    if (!hasAll) {
      setSelectedMedications(next);
      return;
    }

    const lastSelected = next[next.length - 1];
    if (lastSelected?.value === "__all__") {
      setSelectedMedications([ALL_OPTION]);
      return;
    }

    setSelectedMedications(next.filter((option) => option.value !== "__all__"));
  };

  const buildExportHeaders = () => {
    const token = getToken();
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...getJourneyHeaders(),
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const cachedProvider = localStorage.getItem("selectedProvider");
    if (cachedProvider) {
      try {
        const parsed = JSON.parse(cachedProvider);
        if (parsed?.value) {
          headers.sp_id = parsed.value;
        }
      } catch (e) {
        // ignore
      }
    }
    return headers;
  };

  const runExport = async (format) => {
    if (!participantInfo?.uuid) {
      return;
    }
    if (!fromDate || !toDate) {
      toast.error("Please select a start and end date.");
      return;
    }
    if (moment(toDate).isBefore(moment(fromDate), "day")) {
      toast.error("End date must be on or after start date.");
      return;
    }
    if (moment(toDate).isAfter(moment(fromDate).add(3, "months"), "day")) {
      toast.error("Date range must not exceed 3 months.");
      return;
    }

    const hasAll = selectedMedications?.length === 1 && selectedMedications[0]?.value === "__all__";
    if (!hasAll && selectedMedications.length === 0) {
      toast.error("Select at least one medication or All.");
      return;
    }

    const payload = {
      pt_id: participantInfo.uuid,
      duration_start: fromDate,
      duration_end: toDate,
      format,
      medication_uuids: hasAll ? [] : selectedMedications.map((m) => m.value),
    };

    const exportURL = roleAccess([USER_ROLE?.Sp_Standard_User])
      ? URL_PARTICIPANT_MEDICATION_HISTORY_EXPORT_HRM
      : URL_PARTICIPANT_MEDICATION_HISTORY_EXPORT;

    setExporting(true);
    try {
      const response = await axios.post(exportURL, payload, {
        headers: buildExportHeaders(),
        validateStatus: (status) => status < 600,
      });

      if (response.status >= 200 && response.status < 300) {
        const downloadUrl = response.data?.data?.download_url;
        const fileName = response.data?.data?.file_name;
        if (!downloadUrl) {
          toast.error("Export failed. Please try again.");
          return;
        }
        const started = await downloadMedicationHistoryExport(
          downloadUrl,
          format,
          fileName
        );
        if (!started) {
          toast.error("Export failed. Please try again.");
          return;
        }
        toast.success("Export ready.");
        return;
      }

      const message =
        response.data?.message ||
        response.data?.error ||
        "Export failed. Please try again.";
      toast.error(message);
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const hasDateFilter = Boolean(fromDate && toDate);

  return (
    <main className="w-full max-w-full px-4 sm:px-6 py-4 sm:py-6">
      <ReportFiltersShell className="!static !top-auto mb-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:gap-4">
          <ReportFilterField label="Medication Name" className="min-w-0 flex-1">
            <Select
              instanceId="medication-export-filter"
              isMulti
              closeMenuOnSelect={false}
              options={medicationOptions}
              value={selectedMedications}
              onChange={handleMedicationFilterChange}
              placeholder={medicationOptionsLoading ? "Loading…" : "Select..."}
              isLoading={medicationOptionsLoading}
              isDisabled={medicationOptionsLoading || !participantInfo?.uuid}
              isClearable={false}
              isSearchable
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              classNamePrefix="med-export-filter"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: 38,
                  fontSize: 12,
                  borderColor: "#e5e7eb",
                }),
                menuPortal: (base) => ({ ...base, zIndex: 10060 }),
                menu: (base) => ({ ...base, zIndex: 10060 }),
                multiValue: (base) => ({ ...base, fontSize: 12 }),
                option: (base) => ({ ...base, fontSize: 12 }),
              }}
            />
          </ReportFilterField>
          <ReportFilterField
            label="Date Range (Max 3 Months)"
            required
            className="min-w-0 w-full lg:w-[320px] lg:shrink-0"
          >
            <DateRangePickerNew
              isDisabled={loader || exporting}
              showCalendarIcon
              isClearable={true}
              placeholder="DD/MM/YYYY To DD/MM/YYYY"
              handleChange={handleDateChange}
              fromDate={fromDate}
              toDate={toDate}
              handleCancel={() => {
                setFromDate(null);
                setToDate(null);
                setSelectedFromDate(null);
                setMedicinList([]);
              }}
              setSelectedFromDate={setSelectedFromDate}
              maxDate={selectedFromDate ? addMonths(selectedFromDate, 3) : null}
            />
          </ReportFilterField>
          <div className="flex shrink-0 flex-wrap items-center gap-2 lg:pb-0.5">
            <CustomButton
              Icon={<BsFilterCircle className="h-4 w-4" />}
              clickHandler={handleSearch}
              label="Search"
              showLoading={loader || exporting}
              isDisabled={loader || exporting || !fromDate || !toDate}
            />
            <ReportExportButton
              isLoading={loader || exporting}
              onExport={runExport}
            />
          </div>
        </div>
      </ReportFiltersShell>

      {loader ? (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm py-12">
          <div className="flex items-center justify-center">
            <CircularLoader />
          </div>
        </div>
      ) : medicinList?.length > 0 ? (
        <div className="space-y-6">
          {medicinList?.map((item, index) => (
            <div className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden" key={index}>
              <HistoryTable
                item={item}
                toDate={toDate}
                fromDate={fromDate}
                participantInfo={participantInfo}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex w-full min-h-[280px] items-center justify-center rounded-xl border border-gray-200 bg-white px-6 py-12 shadow-sm">
          <div className="flex w-full max-w-md flex-col items-center justify-center text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            {!hasDateFilter ? (
              <>
                <p className="font-medium text-gray-500">
                  Select a date range and click Search to view medication history
                </p>
                <p className="mt-1 text-sm text-gray-400">
                  Use the filters above to load records
                </p>
              </>
            ) : (
              <>
                <p className="font-medium text-gray-500">No medication history found</p>
                <p className="mt-1 text-sm text-gray-400">Add medications to view history</p>
              </>
            )}
          </div>
        </div>
      )}

    </main>
  );
};

export default MedicationHistory;
